import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH28_QUESTIONS } from "../data/neetPhysicsCh28Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 28: Nuclei (NCERT Class 12, Ch. 13).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Nuclear Composition & Size", "Mass Defect & Binding Energy", "Radioactivity & Decay Law", "Nuclear Fission & Fusion"];

const SECTION_COLORS = {
  "Nuclear Composition & Size":   "#0891b2",
  "Mass Defect & Binding Energy": "#d97706",
  "Radioactivity & Decay Law":    "#be123c",
  "Nuclear Fission & Fusion":     "#4d7c0f",
};

const CONCEPTS = [
  // ── Nuclear Composition & Size ─────────────────────────────────────────────
  { section: "Nuclear Composition & Size", title: "Composition of the nucleus — protons, neutrons and nucleons",
    explanation: "The nucleus is made up of protons (charge +e, mass ~1.007276 u) and neutrons (no charge, mass ~1.008665 u), collectively called nucleons. Atomic number Z = number of protons (= number of electrons in a neutral atom). Mass number A = total number of nucleons = Z + N, where N is the neutron number. A nuclide is written as ᴬZX.",
    example: `Notation: ᴬZX  (A = mass number, Z = atomic number)

For uranium-238, ²³⁸₉₂U:
Z (protons)  = 92
N (neutrons) = A - Z = 238 - 92 = 146
A (nucleons) = Z + N = 92 + 146 = 238` },
  { section: "Nuclear Composition & Size", title: "Isotopes, isobars and isotones",
    explanation: "Isotopes: nuclei of the SAME element (same Z), different A (different N) — identical chemical behaviour, different nuclear mass. Isobars: nuclei with the SAME mass number A but different Z (different elements). Isotones: nuclei with the SAME neutron number N, but different Z and A.",
    example: `Isotopes (Z = 1, hydrogen family):
  ¹H (N=0), ²H/deuterium (N=1), ³H/tritium (N=2)

Isobars (A = 14):
  ¹⁴₆C  and  ¹⁴₇N        <- same A, different Z

Isotones (N = 8):
  ¹⁴₆C  and  ¹⁶₈O        <- same N, different Z and A` },
  { section: "Nuclear Composition & Size", title: "Nuclear size — R = R₀A^(1/3)",
    explanation: "Scattering experiments show a nucleus behaves like a sphere of radius R = R0.A^(1/3), where R0 ~ 1.2 fm (1 fm = 10^-15 m) is roughly the same for all nuclei. Since R^3 is proportional to A, nuclear VOLUME is directly proportional to the number of nucleons -- consistent with a nucleus being a tightly packed sphere of nucleons of essentially fixed size.",
    example: `R = R0 . A^(1/3),  R0 = 1.2 fm

Aluminium-27 (A = 27): R = 1.2 x 27^(1/3) = 1.2 x 3   = 3.6 fm
Uranium-238  (A = 238): R = 1.2 x 238^(1/3) ~ 1.2 x 6.2 = 7.4 fm

(A increases ~9x, but R increases only ~2x -- because R ~ A^(1/3))` },
  { section: "Nuclear Composition & Size", title: "Nuclear density is (almost) constant for all nuclei",
    explanation: "Nuclear density = (nuclear mass) / (nuclear volume) = (A.m) / [(4/3)pi.R0^3.A], where m is the average mass of one nucleon. The factor A cancels top and bottom, so density does NOT depend on A -- every nucleus, light or heavy, has essentially the SAME density, roughly 2.3x10^17 kg/m^3, about 10^14 times denser than ordinary solid matter.",
    example: `density = 3m / (4.pi.R0^3),  independent of A

Using m = 1.67x10^-27 kg, R0 = 1.2x10^-15 m:
density ~ 3(1.67x10^-27) / [4.pi.(1.2x10^-15)^3]
        ~ 2.3 x 10^17 kg/m^3   <- same value for A = 27 or A = 238` },
  { section: "Nuclear Composition & Size", title: "Nuclear force — short-range, strong, charge-independent",
    explanation: "The nuclear (strong) force binding nucleons together is the strongest of the fundamental forces at nuclear separations, but acts only over a very SHORT RANGE (roughly 1-3 fm) -- beyond about 3 fm it becomes negligible. It is CHARGE-INDEPENDENT (p-p, n-n and n-p nuclear attractions are approximately equal) and SATURATING -- a nucleon interacts strongly only with its nearest neighbours, not with every other nucleon in the nucleus.",
    example: `Range comparison (schematic):
Nuclear force:  strong, dominant only for separation < ~3 fm
Coulomb force:  weaker at fm range, but falls off much more slowly
                (1/r^2) and so dominates again at larger separations

Saturation -> BE/A does not keep growing with A; it levels off` },

  // ── Mass Defect & Binding Energy ────────────────────────────────────────────
  { section: "Mass Defect & Binding Energy", title: "Mass–energy equivalence and the atomic mass unit (u)",
    explanation: "Einstein's relation E = mc^2 shows mass and energy are interconvertible. Nuclear masses are conveniently measured in atomic mass units (u), where 1 u = 1/12 of the mass of a carbon-12 atom = 1.660539x10^-27 kg. Converting this mass to its energy equivalent gives 1 u = 931.5 MeV -- the standard conversion factor used throughout nuclear physics.",
    example: `E = mc^2

1 u = 1.660539x10^-27 kg
E   = (1.660539x10^-27 kg) x (3x10^8 m/s)^2
    = 1.4924x10^-10 J
    = 1.4924x10^-10 / 1.602x10^-19  eV
    = 9.315x10^8 eV = 931.5 MeV` },
  { section: "Mass Defect & Binding Energy", title: "Mass defect — Δm = [Zm(H) + Nm(n)] − M(nucleus)",
    explanation: "The measured mass of any bound nucleus is always LESS than the sum of the masses of its separate constituent protons and neutrons. This missing mass, the mass defect Δm, is the mass equivalent of the energy released when the nucleons bind together (using atomic masses for Z hydrogen atoms and the parent atom automatically cancels the electron masses on both sides).",
    example: `Δm = [Z.m(¹H) + N.m(n)] - M(atom)

For helium-4 (Z=2, N=2):
m(¹H) = 1.007825 u,  m(n) = 1.008665 u,  M(⁴He) = 4.002603 u

Δm = [2(1.007825) + 2(1.008665)] - 4.002603
   = 4.032980 - 4.002603 = 0.030377 u  ~ 0.0304 u` },
  { section: "Mass Defect & Binding Energy", title: "Binding energy — BE = Δmc²",
    explanation: "Binding energy is the energy equivalent of the mass defect: BE = Δm.c^2. It equals the energy that must be SUPPLIED to completely separate a nucleus into free, isolated protons and neutrons -- or, equivalently, the energy RELEASED when free nucleons come together to form the nucleus.",
    example: `BE = Δm x 931.5 MeV/u

For helium-4:  Δm = 0.030377 u
BE = 0.030377 x 931.5 MeV ~ 28.3 MeV

(This is the energy needed to break one He-4 nucleus into
 2 free protons + 2 free neutrons.)` },
  { section: "Mass Defect & Binding Energy", title: "Binding energy per nucleon (BE/A)",
    explanation: "Total binding energy alone doesn't show which nuclei are most stable, since heavier nuclei naturally have more total BE just from having more nucleons. Dividing by A gives BE/A, the AVERAGE energy binding one nucleon -- a much better indicator of nuclear stability. A higher BE/A means the nucleus is more tightly bound and more stable.",
    example: `BE/A = (total binding energy) / (mass number)

Helium-4: BE ~ 28.3 MeV, A = 4   -> BE/A ~ 7.08 MeV/nucleon
Iron-56:  BE ~ 492  MeV, A = 56  -> BE/A ~ 8.79 MeV/nucleon
                                     (near the maximum of the curve)` },
  { section: "Mass Defect & Binding Energy", title: "The BE/A vs mass number curve — key features & what it tells us",
    explanation: "Plotting BE/A against A gives a curve that rises steeply for the lightest nuclei (with local peaks, e.g. at He-4), climbs to a broad MAXIMUM of about 8.7-8.8 MeV/nucleon around A ~ 56-62 (the iron/nickel region -- the most stable nuclei known), then DECREASES slowly for heavier nuclei, down to about 7.6 MeV/nucleon at uranium. Because the curve rises going from very light nuclei toward the peak, FUSION of light nuclei releases energy; because it falls going from the peak toward very heavy nuclei, FISSION of heavy nuclei into medium-mass fragments also releases energy -- both processes move nucleons toward the higher-BE/A peak.",
    example: `Approx. BE/A values (MeV/nucleon):
Deuterium (A=2):  ~1.1     <- far below peak
Helium-4  (A=4):  ~7.1
Iron-56   (A=56): ~8.8     <- peak (most stable)
Uranium-235(A=235): ~7.6   <- below peak, on the falling side

Fusion (light -> medium):  moves UP the curve  -> releases energy
Fission (heavy -> medium): moves UP the curve  -> releases energy` },

  // ── Radioactivity & Decay Law ───────────────────────────────────────────────
  { section: "Radioactivity & Decay Law", title: "Radioactivity — spontaneous nuclear disintegration",
    explanation: "Radioactivity is the spontaneous emission of alpha, beta, or gamma radiation from unstable nuclei, discovered by Henri Becquerel. It is a purely nuclear process -- unaffected by the atom's temperature, pressure, or chemical environment. For any single nucleus, exactly when it decays is fundamentally random; only for a LARGE number of identical nuclei does decay follow a precise statistical law.",
    example: `Radioactivity is:
- Spontaneous (no external trigger needed)
- Random for an individual nucleus (unpredictable instant of decay)
- Statistical for a large sample (follows N = N0.e^(-lambda.t))
- Unaffected by temperature, pressure, or chemical combination` },
  { section: "Radioactivity & Decay Law", title: "Alpha (α) decay",
    explanation: "In alpha decay, a nucleus emits an alpha particle -- a helium-4 nucleus (2 protons + 2 neutrons) -- reducing the parent's atomic number Z by 2 and mass number A by 4. It occurs mainly in heavy nuclei, helping them shed excess nucleons and move toward a more stable configuration.",
    example: `ᴬZX  ->  ᴬ⁻⁴Z₋₂Y  +  ⁴₂He (alpha particle)

²³⁸₉₂U  ->  ²³⁴₉₀Th  +  ⁴₂He
Z: 92 -> 90     A: 238 -> 234` },
  { section: "Radioactivity & Decay Law", title: "Beta decay (β⁻ and β⁺)",
    explanation: "In β⁻ decay, a neutron inside the nucleus converts into a proton, emitting an electron (β⁻ particle) and an antineutrino: n -> p + e⁻ + antineutrino. This increases Z by 1, while A stays unchanged. In β⁺ decay, a proton converts into a neutron, emitting a positron and a neutrino: p -> n + e⁺ + neutrino, decreasing Z by 1 with A unchanged. (A closely related process, electron capture, has the nucleus absorb an orbital electron: p + e⁻ -> n + neutrino, also decreasing Z by 1.)",
    example: `β⁻ decay:  n -> p + e⁻ + antineutrino     (Z increases by 1)
¹⁴₆C  ->  ¹⁴₇N + β⁻ + antineutrino     (Z: 6 -> 7, A: 14 -> 14)

β⁺ decay:  p -> n + e⁺ + neutrino         (Z decreases by 1)
²²₁₁Na ->  ²²₁₀Ne + β⁺ + neutrino      (Z: 11 -> 10, A: 22 -> 22)` },
  { section: "Radioactivity & Decay Law", title: "Gamma (γ) decay",
    explanation: "Alpha or beta decay often leaves the daughter nucleus in an excited energy state. The excited nucleus de-excites to its ground state by emitting a high-energy photon (a gamma ray), with NO change in Z or A -- it is purely a release of the nucleus's excess internal energy, similar to how an atom emits light when an electron falls to a lower orbit, but at much higher (MeV-scale) energies.",
    example: `ᴬZX*  (excited)  ->  ᴬZX (ground state) + gamma photon

Cobalt-60 decays by β⁻ to an EXCITED nickel-60 nucleus, which
then emits two gamma photons (~1.17 MeV and ~1.33 MeV) to reach
its ground state:  ⁶⁰₂₈Ni*  ->  ⁶⁰₂₈Ni  +  γ + γ
(Z and A are unchanged in this gamma-emission step.)` },
  { section: "Radioactivity & Decay Law", title: "Law of radioactive decay — N = N₀e^(−λt)",
    explanation: "The rate of decay of a large sample is proportional to the number of undecayed nuclei present: dN/dt = -λN, where λ is the decay constant (the probability of decay per nucleus per unit time, characteristic of the nuclide). Integrating gives the exponential decay law N = N0.e^(-λt), where N0 is the number of nuclei at t = 0. Two related timescales follow from λ: the HALF-LIFE T1/2 (time for half the sample to decay) = ln(2)/λ = 0.693/λ, and the MEAN LIFE (average lifetime of a nucleus) τ = 1/λ -- so τ = T1/2 / 0.693 ~ 1.44 x T1/2, meaning mean life is always somewhat longer than half-life. After n half-lives, the fraction of the original sample remaining is (1/2)^n.",
    example: `dN/dt = -λN   =>   N = N0.e^(-λt)

If N0 = 1000 nuclei and λ = 0.1 per second,
after t = 10 s:  N = 1000 x e^(-0.1x10) = 1000 x e^-1
              = 1000 x 0.368 ~ 368 nuclei remain undecayed

T1/2 = 0.693/λ          τ = 1/λ = 1.44 x T1/2
If T1/2 = 10 days:  λ = 0.0693/day,  τ ~ 14.4 days

After 3 half-lives: fraction remaining = (1/2)^3 = 1/8 = 12.5%
(e.g. Carbon-14, T1/2 = 5730 yr: after 17,190 yr, 12.5% remains)` },
  { section: "Radioactivity & Decay Law", title: "Activity — A = λN, becquerel and curie",
    explanation: "Activity is the rate of decay of a sample -- the number of disintegrations occurring per second: A = λN(t) = λN0.e^(-λt) = A0.e^(-λt); activity decays with the SAME half-life as N itself. The SI unit is the becquerel (Bq): 1 Bq = 1 decay per second. The older practical unit is the curie (Ci): 1 Ci = 3.7x10^10 decays per second (approximately the activity of 1 gram of radium-226).",
    example: `A = λN

If N = 6.0x10^18 undecayed nuclei and λ = 1.0x10^-8 per second:
A = (1.0x10^-8)(6.0x10^18) = 6.0x10^10 decays/s = 6.0x10^10 Bq

In curies: 6.0x10^10 / 3.7x10^10 ~ 1.62 Ci` },

  // ── Nuclear Fission & Fusion ─────────────────────────────────────────────────
  { section: "Nuclear Fission & Fusion", title: "Nuclear fission",
    explanation: "Nuclear fission is the splitting of a heavy nucleus (such as uranium-235) into two lighter fission fragments of roughly comparable mass, after it absorbs a slow (thermal) neutron and becomes an unstable compound nucleus. Each fission event also releases 2-3 fast neutrons and a large amount of energy (~200 MeV per fission), mostly as the kinetic energy of the fragments. It was discovered by Otto Hahn and Fritz Strassmann in 1938.",
    example: `n + ²³⁵₉₂U  ->  ¹⁴¹₅₆Ba + ⁹²₃₆Kr + 3n  +  ~200 MeV

Check mass number: 235 + 1 = 236;  141 + 92 + 3(x1) = 236  ✓
Check atomic number: 92 = 56 + 36 (neutrons carry no charge) ✓` },
  { section: "Nuclear Fission & Fusion", title: "Chain reaction & critical mass",
    explanation: "Since each fission releases 2-3 neutrons, and each of those can trigger further fissions in nearby fissile nuclei, a SELF-SUSTAINING CHAIN REACTION becomes possible. The neutron multiplication factor k is the average number of neutrons from one fission that go on to cause another: k = 1 gives a steady, controlled reaction (as in a reactor); k > 1 gives an uncontrolled, rapidly growing reaction (as in a bomb); k < 1 causes the reaction to die out. CRITICAL MASS is the minimum amount of fissile material needed for k = 1 -- below it, too many neutrons escape the surface or are absorbed without causing further fission.",
    example: `k < 1  -> subcritical  (reaction dies out)
k = 1  -> critical     (steady, self-sustaining -- reactor)
k > 1  -> supercritical (rapidly growing -- uncontrolled)

Natural uranium: 99.3% U-238 (not readily fissile by slow n)
                  0.7% U-235 (fissile) -> reactors use ENRICHED
                  fuel (~3-5% U-235) + a MODERATOR (heavy water or
                  graphite) to slow fast neutrons + CONTROL RODS
                  (cadmium/boron) to absorb excess neutrons and
                  hold k ~ 1 for steady, controlled power output.` },
  { section: "Nuclear Fission & Fusion", title: "Nuclear fusion",
    explanation: "Nuclear fusion is the combining of two light nuclei into a single heavier, more tightly bound nucleus, releasing energy (moving up the BE/A curve from its low, light-nucleus end). Because both nuclei are positively charged, they strongly repel each other electrostatically (the Coulomb barrier); fusion needs extremely high temperature (~10^7 K or more) and pressure/density so nuclei have enough kinetic energy to get close enough (~fm range) for the short-range nuclear force to take over and bind them -- hence fusion reactions are also called thermonuclear reactions.",
    example: `²₁H + ³₁H  ->  ⁴₂He + n  +  17.6 MeV   (deuterium-tritium fusion)
²₁H + ²₁H  ->  ³₂He + n  +  3.27 MeV   (one deuterium-deuterium branch)

D-T fusion needs temperatures of ~10^8 K to overcome the Coulomb
barrier between the two positively charged nuclei -- it is the
easiest fusion reaction to achieve and the basis of most planned
controlled-fusion reactor designs on Earth.` },
  { section: "Nuclear Fission & Fusion", title: "Fusion as the energy source of stars",
    explanation: "The Sun and most stars generate their energy through the PROTON-PROTON (p-p) CYCLE: through a sequence of steps, 4 hydrogen (¹H) nuclei ultimately fuse into 1 helium-4 nucleus, releasing 2 positrons, 2 neutrinos, and energy carried mainly as gamma rays and the kinetic energy of the products. The Sun's core, at an extreme temperature of ~1.5x10^7 K and enormous pressure, sustains a colossal rate of such fusions, powering its luminosity for billions of years -- the same basic reaction scientists aim to reproduce and control in fusion reactors on Earth.",
    example: `Net p-p cycle reaction:
4 ¹₁H  ->  ⁴₂He  +  2e⁺  +  2ν  +  energy (~26.7 MeV per cycle)

Roughly: 4 protons -> 1 helium-4 nucleus, releasing energy that
eventually escapes the Sun as sunlight (visible light + heat).` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh28Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh28Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh28Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH28_QUESTIONS : NEET_PHYSICS_CH28_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 28</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Nuclei</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 13 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH28_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH28_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
